import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { Store } from '@ngrx/store';
import { map, merge, take, tap, withLatestFrom } from 'rxjs';
import {
  createClient,
  createLink,
  deleteLink,
  selectClients,
  selectPersonalState,
  selectUserWheels,
  updateClient,
} from '../../../../state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface CreateClientModalData {
  mode: 'create' | 'edit';
  client?: ParticipantOutputWithLinksDto;
}

type ClientFormGroup = FormGroup<{
  name: FormControl<string>;
  notes: FormControl<string>;
  selectedWheel: FormControl<string>;
}>;

@Component({
  selector: 'wil-create-client-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    ClipboardModule,
  ],
  templateUrl: './create-client-modal.component.html',
  styleUrls: ['./create-client-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateClientModalComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<CreateClientModalComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  public readonly data = inject<CreateClientModalData>(MAT_DIALOG_DATA);

  public readonly clientForm: ClientFormGroup;
  public readonly mode: 'create' | 'edit';
  public modalTitle = 'Add Client';

  // Store selectors
  public readonly wheels$ = this.store.select(selectUserWheels);
  public readonly hasWheelsAvailable$ = this.wheels$.pipe(map((wheels) => wheels.length > 0));
  public readonly personalState$ = this.store.select(selectPersonalState);
  public readonly clients$ = this.store.select(selectClients).pipe(
    tap((clients) => {
      const id = this.data.client?.id || this.savedClientId();
      if (id) {
        const links: ResolvedWheelLink[] = clients.find((c) => c.id === id)?.assignedWheelsResolved || [];
        this.generatedLinks.set(links);
      }
    }),
    withLatestFrom(this.personalState$),
    tap(([clients, state]) => {
      // update component state after creating or updating a client
      if (this.isSavingClient()) {
        if (!state.error) {
          this.clientSaved.set(true);
          // todo dodgy AF
          this.savedClientId.set(clients[clients.length - 1].id);
        } else {
          // TODO: Show error message to user
        }
      }

      this.isModified.set(false);
      this.isSavingClient.set(false);
    }),
    tap(() => {
      // update component state after link generation
      this.generatingLink.set(false);
    })
  );

  public readonly generatingLink = signal(false);
  public readonly generatedLinks = signal<ResolvedWheelLink[]>([]);
  public readonly isModified = signal(false);
  public readonly isSavingClient = signal(false);

  // Track if client has been saved (for create mode)
  public readonly clientSaved = signal(false);
  public readonly savedClientId = signal<string | null>(null);

  constructor() {
    const existingClient = this.data?.client;

    // mode is set only on init
    this.mode = this.data.mode;
    this.updateTitle();

    this.clientForm = this.fb.group({
      name: new FormControl(existingClient?.name || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(80)],
      }),
      notes: new FormControl(existingClient?.notes || '', { nonNullable: true }),
      selectedWheel: new FormControl('', { nonNullable: true }),
    });

    // Watch for form changes - only track user changes to name and notes fields
    // Skip initial values (which are set programmatically from database)
    const { name, notes } = this.clientForm.controls;
    merge(name.valueChanges, notes.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isModified.set(existingClient?.name !== name.value || existingClient?.notes !== notes.value);
      });

    // In edit mode, client is already saved
    if (this.mode === 'edit' && existingClient?.id) {
      this.clientSaved.set(true);
      this.savedClientId.set(existingClient.id);
    }
  }

  ngOnInit(): void {
    this.clients$.subscribe();
  }

  ngOnDestroy(): void {
    this.savedClientId.set(null);
    this.generatingLink.set(false);
    this.generatedLinks.set([]);
    this.isModified.set(false);
    this.isSavingClient.set(false);
    this.clientSaved.set(false);
  }

  public get isWheelSelected(): boolean {
    return !!this.clientForm.controls.selectedWheel.value;
  }

  // todo add interceptor to catch errors instead of try catch for every request
  public onSaveClient(): void {
    if (!this.clientForm.valid) return;

    this.isSavingClient.set(true);

    const clientData: CreateParticipantDto = {
      name: this.clientForm.controls.name.value.trim(),
      notes: this.clientForm.controls.notes?.value.trim() || undefined,
    };
    // todo dodgy AF - mode cant ever change or this breaks
    if (this.mode === 'create' && !this.clientSaved()) {
      this.store.dispatch(createClient({ client: clientData }));
    } else if (this.mode === 'edit') {
      this.store.dispatch(updateClient({ id: this.data!.client!.id, updates: clientData }));
    } else {
      // In create mode, client already saved, just close
      this.onCancel();
    }
  }

  public async onGenerateLink(): Promise<void> {
    const selectedWheelId = this.clientForm.controls.selectedWheel.value;

    if (!selectedWheelId) return;

    const participantId = this.savedClientId() || this.data?.client?.id;
    if (!participantId) {
      console.error('No client ID available for link generation');
      return;
    }

    this.generatingLink.set(true);

    this.wheels$.pipe(withLatestFrom(this.clients$), take(1)).subscribe(([wheels, [participants]]) => {
      const wheelVersionId = wheels.find((w) => w.id === selectedWheelId)?.wheelVersionId || '0'; // todo need proper business logic
      const assignedWheels = participants.find((p) => p.id === participantId)?.assignedWheels;

      this.store.dispatch(
        createLink({
          participantId: participantId,
          link: {
            wheelId: selectedWheelId,
            wheelVersionId,
            maxUses: 100,
          },
          assignedWheels: assignedWheels?.length ? [...assignedWheels, selectedWheelId] : [selectedWheelId],
        })
      );

      // Reset wheel selection
      this.clientForm.controls.selectedWheel.patchValue('');
    });
  }

  public async onDeleteLink(link: WheelLink): Promise<void> {
    this.store.dispatch(deleteLink({ id: link.id }));
  }

  public onCancel(): void {
    if (this.isModified()) {
      const confirmDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
      if (!confirmDiscard) {
        return;
      }
    }
    this.dialogRef.close({ action: 'cancel' });
  }

  // Custom validator for name field
  public getNameErrorMessage(): string {
    const nameControl = this.clientForm.get('name');

    if (nameControl?.hasError('required') || nameControl?.hasError('minlength') || nameControl?.hasError('maxLength')) {
      return 'Enter a valid name (2–80 characters).';
    }

    return '';
  }

  public onCopyLink(): void {
    // The copy functionality is handled by the ClipboardModule directive
  }

  private updateTitle(): void {
    this.modalTitle = this.mode === 'create' ? 'Add Client' : 'Manage Client';
  }
}
